import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateProjectDto, Project, UpdateProjectDto } from 'api';
import { clientService, projectService } from 'api';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { queryKeys } from 'shared/constants/queryKeys';

interface DeleteDialogState {
  open: boolean;
  projectId: string | null;
}

interface ProjectModalState {
  open: boolean;
  projectId: string | null;
}

/**
 * Project list + the Edit/Archive/Delete flows for a single project — extracted so
 * ProjectsPage (list) and ClientProjectsTab (Client Details page) drive the exact same
 * modal, dialog, mutations, and cache invalidation instead of each having its own copy.
 */
export function useProjectActions() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    projectId: null,
  });
  const [projectModal, setProjectModal] = useState<ProjectModalState>({
    open: false,
    projectId: null,
  });

  const { data: projects = [], isLoading } = useQuery({
    queryKey: queryKeys.projects.listWithTrackedHours(),
    queryFn: () => projectService.getProjectsWithTrackedHours().then((res) => res.data.data),
  });

  const { data: clients = [] } = useQuery({
    queryKey: queryKeys.clients.list(),
    queryFn: () => clientService.getClients().then((res) => res.data.data),
  });

  const closeModal = () => setProjectModal({ open: false, projectId: null });

  const createMutation = useMutation({
    mutationFn: (data: CreateProjectDto) => projectService.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      closeModal();
      enqueueSnackbar('Project created', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to create project', { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectDto }) =>
      projectService.updateProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      closeModal();
      enqueueSnackbar('Project updated', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to update project', { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => projectService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      enqueueSnackbar('Project deleted', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to delete project', { variant: 'error' });
    },
  });

  // ── Edit ──────────────────────────────────────────────────────────────────

  const openCreateModal = () => setProjectModal({ open: true, projectId: null });
  const openEditModal = (projectId: string) => setProjectModal({ open: true, projectId });

  const handleModalSubmit = (data: CreateProjectDto | UpdateProjectDto) => {
    if (projectModal.projectId) {
      updateMutation.mutate({ id: projectModal.projectId, data: data as UpdateProjectDto });
    } else {
      createMutation.mutate(data as CreateProjectDto);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────

  const requestDelete = (projectId: string) => setDeleteDialog({ open: true, projectId });

  /** @param onDeleted Extra side effect run only on a successful delete. */
  const confirmDelete = (onDeleted?: () => void) => {
    if (!deleteDialog.projectId) return;
    deleteMutation.mutate(deleteDialog.projectId, {
      onSuccess: () => onDeleted?.(),
      onSettled: () => setDeleteDialog({ open: false, projectId: null }),
    });
  };

  const cancelDelete = () => setDeleteDialog({ open: false, projectId: null });

  const getProject = (projectId: string): Project | undefined =>
    projects.find((p) => p.id === projectId);

  return {
    projects,
    clients,
    isLoading,
    getProject,

    projectModal,
    openCreateModal,
    openEditModal,
    closeModal,
    handleModalSubmit,
    createMutation,
    updateMutation,

    deleteDialog,
    requestDelete,
    confirmDelete,
    cancelDelete,
    deleteMutation,
  };
}
